import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { fetchProductsByBrand } from "../Redux/slice/productSlice";
import { Button } from "antd";

const BRAND_ID = "760af684-7a19-46ab-acc5-7445ef32073a";
const backendBaseURL = "https://smfteapi.salesmate.app";

const SamsungDeals = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const containerRef = useRef(null);

  const [showArrows, setShowArrows] = useState(false);
  const [imageLoading, setImageLoading] = useState({});

  const { brandProducts = [], loading } = useSelector((state) => state.products);

  useEffect(() => {
    dispatch(fetchProductsByBrand(BRAND_ID));
  }, [dispatch]);

  useEffect(() => {
    const updateArrows = () => {
      if (containerRef.current) {
        setShowArrows(containerRef.current.scrollWidth > containerRef.current.clientWidth);
      }
    };

    updateArrows();
    window.addEventListener("resize", updateArrows);
    return () => window.removeEventListener("resize", updateArrows);
  }, [brandProducts]);

  const scrollLeft = () => containerRef.current?.scrollBy({ left: -200, behavior: "smooth" });
  const scrollRight = () => containerRef.current?.scrollBy({ left: 200, behavior: "smooth" });

  const handleImageLoad = (productId) => {
    setImageLoading((prev) => ({ ...prev, [productId]: false }));
  };

  const handleImageError = (productId) => {
    setImageLoading((prev) => ({ ...prev, [productId]: true }));
  };

  const formatPrice = (price) =>
    price?.toLocaleString(undefined, { minimumFractionDigits: 2 });

  const filteredProducts = Array.isArray(brandProducts)
    ? brandProducts.filter((item) => item.productID)
    : [];

  return (
    <div className="container mx-auto px-2 mt-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-2 bg-green-600 p-2 rounded-md">
        <h2 className="text-sm flex items-center text-white font-semibold">
          <span className="mr-2">📱</span> Official Samsung Products
        </h2>
        <button
          className="text-sm text-white hover:underline flex items-center"
          onClick={() => navigate("/product/samsung")}
        >
          Visit Store <RightOutlined className="ml-1" />
        </button>
      </div>

      {/* Product Carousel */}
      <div className="relative">
        {showArrows && (
          <>
            <Button
              icon={<LeftOutlined />}
              shape="circle"
              className="hidden sm:block absolute top-1/2 -translate-y-1/2 left-2 z-10 bg-white text-gray-800 shadow-md hover:bg-gray-200 transition-all duration-300"
              onClick={scrollLeft}
            />
            <Button
              icon={<RightOutlined />}
              shape="circle"
              className="hidden sm:block absolute top-1/2 -translate-y-1/2 right-2 z-10 bg-white text-gray-800 shadow-md hover:bg-gray-200 transition-all duration-300"
              onClick={scrollRight}
            />
          </>
        )}

        <div
          ref={containerRef}
          className="flex gap-4 overflow-x-auto no-scrollbar scrollbar-hide px-2"
        >
          {loading ? (
            [...Array(6)].map((_, index) => (
              <div
                key={index}
                className="bg-white p-3 rounded-lg shadow-md min-w-[150px] md:min-w-[200px] lg:min-w-[250px] animate-pulse"
              >
                <div className="w-32 md:w-24 lg:w-48 h-32 bg-gray-300 rounded-lg"></div>
                <div className="h-4 bg-gray-300 rounded w-3/4 mt-2"></div>
                <div className="h-3 bg-gray-300 rounded w-1/2 mt-1"></div>
              </div>
            ))
          ) : (
            filteredProducts
              .slice()
              .sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated))
              .map((item) => (
                <div
                  key={item.productID}
                  className="relative bg-white text-black p-4 rounded-lg shadow-md flex flex-col cursor-pointer transform transition-transform duration-300 hover:scale-105 hover:shadow-xl hover:-translate-y-1 min-w-[150px] md:min-w-[200px] lg:min-w-[250px] border border-gray-200"
                  onClick={() => navigate(`/product/${item.productID}`)}
                >
                  {/* Discount Badge */}
                  {item.oldPrice > 0 && (
                    <div className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-1 rounded-full">
                      {`${Math.round(
                        ((item.oldPrice - item.price) / item.oldPrice) * 100
                      )}% OFF`}
                    </div>
                  )}

                  {/* Product Image */}
                  <div className="h-32 md:h-40 lg:h-48 flex items-center justify-center mb-3">
                    {imageLoading[item.productID] ? (
                      <div className="w-32 md:w-24 lg:w-48 h-32 bg-gray-300 rounded-lg animate-pulse"></div>
                    ) : (
                      <img
                        src={`${backendBaseURL}/Media/Products_Images/${item.productImage?.split("\\").pop()}`}
                        alt={item.productName}
                        className="w-32 md:w-24 lg:w-48 object-cover rounded-lg transition-transform duration-300 hover:scale-110"
                        onLoad={() => handleImageLoad(item.productID)}
                        onError={() => handleImageError(item.productID)}
                      />
                    )}
                  </div>

                  {/* Product Info */}
                  <p className="text-xs line-clamp-2">{item.productName}</p>
                  <p className="text-red-600 text-sm">GH₵ {formatPrice(item.price)}</p>
                  {item.oldPrice > 0 && (
                    <p className="text-gray-500 text-xs line-through">
                      GH₵ {formatPrice(item.oldPrice)}
                    </p>
                  )}
                </div>
              ))
          )}
        </div>
      </div>
    </div>
  );
};

export default SamsungDeals;
